/**
 * Client-side sanering van LLM-gegenereerde overlay-regels.
 *
 * De LLM (via `POST /api/v1/ai/overlay-rules`) is gegrond op de workspace-
 * context, maar we vertrouwen z'n output niet blind: hier valideren we elke
 * teruggekomen regel tegen de daadwerkelijk bekende id's/namen en gooien alles
 * weg wat niet klopt (gehallucineerde types, onbekende attributen, ongeldige
 * kleuren). Zo blijft de toegepaste overlay deterministisch en betrouwbaar,
 * ongeacht wat de LLM verzint. Elke overlevende regel krijgt een verse id.
 */
import type {
  BilingualText,
  OverlayAttributeClause,
  OverlayChildClause,
  OverlayComparator,
  OverlayCondition,
  OverlayEffect,
  OverlayRelationClause,
  OverlayRule,
} from "@ae/domain";

export interface OverlayKnownIds {
  types: Set<string>;
  stereotypes: Set<string>;
  layers: Set<string>;
  attributes: Set<string>;
  attributeTypes: Set<string>;
  relationTypes: Set<string>;
}

const VALID_CMP = new Set<string>([
  "eq",
  "neq",
  "contains",
  "in",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "not_exists",
]);

const CHILD_REF_ATTR = "attribute_type_ref";

function isHex(s: unknown): s is string {
  return typeof s === "string" && /^#[0-9a-fA-F]{3,8}$/.test(s);
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  return [];
}

function needsValue(cmp: OverlayComparator): boolean {
  return cmp !== "exists" && cmp !== "not_exists";
}

function sanitizeClause(raw: unknown, knownAttr: Set<string>): OverlayAttributeClause | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const attribute = typeof c.attribute === "string" ? c.attribute : "";
  if (!attribute || !knownAttr.has(attribute)) return null;
  const cmp = typeof c.comparator === "string" ? c.comparator : "";
  if (!VALID_CMP.has(cmp)) return null;
  const comparator = cmp as OverlayComparator;
  const clause: OverlayAttributeClause = { attribute, comparator };
  if (needsValue(comparator)) {
    if (comparator === "in") {
      clause.value = asStringArray(c.value);
    } else if (typeof c.value === "string" || typeof c.value === "number") {
      clause.value = c.value;
    } else {
      return null; // vergelijking vereist een waarde maar er is er geen
    }
  }
  return clause;
}

function sanitizeChild(raw: unknown, known: OverlayKnownIds): OverlayChildClause | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const clause: OverlayChildClause = {};
  const typeIds = asStringArray(c.typeIds).filter((t) => known.types.has(t));
  if (typeIds.length) clause.typeIds = typeIds;
  const stereotypes = asStringArray(c.stereotypes).filter((s) => known.stereotypes.has(s));
  if (stereotypes.length) clause.stereotypes = stereotypes;
  const attrs: OverlayAttributeClause[] = [];
  if (Array.isArray(c.attributes)) {
    for (const rawClause of c.attributes) {
      const rc = rawClause as Record<string, unknown> | null;
      // Speciaal geval: verwijzing naar een attribuuttype-id.
      if (rc && rc.attribute === CHILD_REF_ATTR) {
        const val = typeof rc.value === "string" ? rc.value : "";
        if (val && known.attributeTypes.has(val)) {
          attrs.push({ attribute: CHILD_REF_ATTR, comparator: "eq", value: val });
        }
        continue;
      }
      const clean = sanitizeClause(rawClause, known.attributes);
      if (clean) attrs.push(clean);
    }
  }
  if (attrs.length) clause.attributes = attrs;
  if (!clause.typeIds && !clause.stereotypes && !clause.attributes) return null;
  return clause;
}

function sanitizeRelation(raw: unknown, known: OverlayKnownIds): OverlayRelationClause | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const clause: OverlayRelationClause = {};
  const relationTypeIds = asStringArray(c.relationTypeIds).filter((t) =>
    known.relationTypes.has(t),
  );
  if (relationTypeIds.length) clause.relationTypeIds = relationTypeIds;
  const otherTypeIds = asStringArray(c.otherTypeIds).filter((t) => known.types.has(t));
  if (otherTypeIds.length) clause.otherTypeIds = otherTypeIds;
  if (typeof c.otherName === "string" && c.otherName.trim()) clause.otherName = c.otherName.trim();
  if (!clause.relationTypeIds && !clause.otherTypeIds && !clause.otherName) return null;
  if (c.direction === "outgoing" || c.direction === "incoming") clause.direction = c.direction;
  return clause;
}

export function sanitizeOverlayRules(raw: unknown[], known: OverlayKnownIds): OverlayRule[] {
  const out: OverlayRule[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const rawCond = (r.condition ?? {}) as Record<string, unknown>;
    const condition: OverlayCondition = {};

    const typeIds = asStringArray(rawCond.typeIds).filter((t) => known.types.has(t));
    if (typeIds.length) condition.typeIds = typeIds;
    const stereotypes = asStringArray(rawCond.stereotypes).filter((s) => known.stereotypes.has(s));
    if (stereotypes.length) condition.stereotypes = stereotypes;
    const layers = asStringArray(rawCond.layers).filter((l) => known.layers.has(l));
    if (layers.length) condition.layers = layers;

    if (Array.isArray(rawCond.attributes)) {
      const attrs: OverlayAttributeClause[] = [];
      for (const rawClause of rawCond.attributes) {
        const clean = sanitizeClause(rawClause, known.attributes);
        if (clean) attrs.push(clean);
      }
      if (attrs.length) condition.attributes = attrs;
    }

    const relation = sanitizeRelation(rawCond.relation, known);
    if (relation) condition.relation = relation;

    const child = sanitizeChild(rawCond.child, known);
    if (child) condition.child = child;

    const rawEff = (r.effect ?? {}) as Record<string, unknown>;
    const effect: OverlayEffect = {};
    if (isHex(rawEff.fill)) effect.fill = rawEff.fill;
    if (isHex(rawEff.stroke)) effect.stroke = rawEff.stroke;
    if (isHex(rawEff.text)) effect.text = rawEff.text;
    if (rawEff.dim === true) effect.dim = true;
    if (
      effect.fill === undefined &&
      effect.stroke === undefined &&
      effect.text === undefined &&
      !effect.dim
    ) {
      continue; // geen zichtbaar effect → nutteloze regel, overslaan
    }

    const rule: OverlayRule = { id: crypto.randomUUID(), condition, effect };
    if (r.label && typeof r.label === "object") {
      const lbl = r.label as Record<string, unknown>;
      const bi: BilingualText = {};
      if (typeof lbl.nl === "string" && lbl.nl.trim()) bi.nl = lbl.nl;
      if (typeof lbl.en === "string" && lbl.en.trim()) bi.en = lbl.en;
      if (Object.keys(bi).length) rule.label = bi;
    }
    out.push(rule);
  }
  return out;
}
